import React from "react";
import { Link, Link as RouterLink } from "react-router-dom";
import { IconButton, makeStyles, Typography } from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import { CompareSharp, Delete, Edit } from "@material-ui/icons";
import styled from "styled-components";
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useTable,
} from "react-table";
import apiHelper from "../../../../util/ApiHelper";
import { CustomCheckbox, modal } from "../../../../util";
import { useLoading } from "../../../../util/LoadingProvider";
import { PaginationHandler } from "../../../../util/useManualPagination";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import PaginationBarForHandler from "../../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  text: {
    textDecoration: "none",
    color: "#306F9D",
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
}));

const statuses = Object.freeze({
  all: null,
  active: true,
  inactive: false,
});

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

const StatusFilter = styled.ul`
  list-style: none;
  display: inline-flex;
  padding: 0;

  li {
    button {
      border: 0px;
      background: transparent;
      color: #306f9d;
      font-size: 13px;
      line-height: 15px;
      padding: 0 10px;
      cursor: pointer;

      &.active {
        font-weight: bold;
        color: #333333;
      }
    }

    &:not(:last-of-type) {
      button {
        border-right: 1px solid black;
      }
    }

    &:first-of-type {
      button {
        padding-left: 0;
      }
    }
  }
`;

const formatDatetime = (dt) => {
  return (dayjs(dt).isValid()) ? dayjs(dt).format("DD MMM YYYY, hh:mma") : '';
};

const BannerListingPage = (props) => {
  const { match, disabled } = props;
  const classes = useStyles();
  const [status, setStatus] = React.useState("all");
  const [actionType, setActionType] = React.useState("");
  const [keyword, setKeyword] = React.useState("");
  const [data, setData] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);

  const { setLoading } = useLoading();

  // for pagination
  const [paginationHandler] = React.useState(new PaginationHandler());
  const [elementCounts, setElementCounts] = React.useState({
    all: 0,
    active: 0,
    inactive: 0,
  });

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const handleSelectChange = (e) => {
    setActionType(e.target.value);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "id",
        accessor: "id",
      },
      {
        Header: "Image",
        accessor: "image",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <img style={{ maxHeight: "150px" }} src={row.original.image} />
          );
        },
      },
      {
        Header: "Image(Mobile)",
        accessor: "imageMobile",
        disableSortBy: true,
        Cell: ({ row }) => {
          return (
            <img style={{ maxHeight: "150px" }} src={row.original.imageMobile} />
          );
        },
      },
      {
        Header: "Name",
        accessor: "name",
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
            >
              {row.original.name}
            </Link>
          );
        },
      },
      {
        Header: "Start Time",
        accessor: "startTime",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return formatDatetime(row.original.startTime);
        },
      },
      {
        Header: "End Time",
        accessor: "endTime",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return formatDatetime(row.original.endTime);
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => {
          return row.original.status ? "Active" : "Inactive";
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    setPageSize,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    selectedFlatRows,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      manualPagination: true,
      pageCount: totalPages,
    },
    useGlobalFilter,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        // Let's make a column for selection
        {
          id: "selection",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div>
              <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        ...columns,
        {
          id: "action",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: () => <div>Action</div>,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                component={CustomLink}
                to={`${match.url}/${row.original.id}`}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                className={classes.iconBtn}
                size="small"
                // component={CustomLink}
                onClick={() => {
                  deleteBanner(row.original.id);
                }}
              >
                <Delete fontSize="inherit" />
              </IconButton>
            </div>
          ),
        },
      ]);
    }
  );

  const getBanner = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getHkiffAcaBanner(
        null,
        0,
        0,
        9999999,
        ""
      );
      console.log("RESULT:", result);

      paginationHandler.setPageIndex(0).setRows(result.data.content);
      const all = result.data.totalElements;
      const totalPages = paginationHandler.getTotalPages();
      const active = paginationHandler
        .getStatuslessRows()
        .filter((x) => x && x.status).length;

      const inactive = paginationHandler
        .getStatuslessRows()
        .filter((x) => x && !x.status).length;

      setElementCounts({
        all,
        active,
        inactive,
      });
      setData(paginationHandler.setPageIndex(0).getRows());
      setTotalPages(Math.ceil(totalPages));
      setStatus("all");
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [paginationHandler, setLoading]);

  React.useEffect(() => {
    if (paginationHandler) {
      getBanner();
    }
  }, [getBanner, paginationHandler]);

  const statusFilterHandler = (targetStatus) => () => {
    console.log("targetStatus", targetStatus);
    setLoading(true);
    setStatus(targetStatus);
    paginationHandler
      .resetRows()
      .setQueryStatus("status", statuses[targetStatus])
      .filterRowsByAll();
    gotoPage(0);
    setData(paginationHandler.setPageIndex(0).getRows());
    setTotalPages(paginationHandler.getTotalPages());
    setLoading(false);
  };

  React.useEffect(() => {
    statusFilterHandler(status);
  }, [status]);

  const triggerSearch = React.useCallback(() => {
    setLoading(true);
    console.log("KEYWORD: " + keyword);
    paginationHandler
      .resetRows()
      .setQueryString(["name"], keyword)
      .filterRowsByAll();
    const all = paginationHandler.getStatuslessRows().length;
    const active = paginationHandler
      .getStatuslessRows()
      .filter((x) => x.status).length;

    const inactive = paginationHandler
      .getStatuslessRows()
      .filter((x) => !x.status).length;
    setElementCounts({
      all,
      active,
      inactive,
    });
    gotoPage(0);
    setData(paginationHandler.setPageIndex(0).getRows());
    setTotalPages(paginationHandler.getTotalPages());
    setLoading(false);
  }, [gotoPage, keyword, paginationHandler, setLoading]);

  const deleteBanner = async (id) => {
    const decision = await modal.fire({
      title: "Delete Banner",
      text: "Are you sure to delete this banner?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteAcaBannerById(id);
        console.log(result);
        if (result.data.code === 0) {
          getBanner();
          toast.dark("Banner deleted.");
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleBulkAction = React.useCallback(async () => {
    switch (actionType) {
      case "DELETE":
        try {
          let ids = [];
          let deleteCount = 0;
          for (var i = 0; i < rows.length; i++) {
            if (rows[i].isSelected) {
              ids.push(rows[i].original.id);
              deleteCount++;
            }
          }
          const decision = await modal.fire({
            title: "Delete Banner",
            text: `Are you sure to delete ${deleteCount} banner(s)?`,
            showCancelButton: true,
            confirmButtonText: `Delete`,
          });
          if (deleteCount > 0 && decision.isConfirmed) {
            try {
              const promises = ids.map((id) =>
                apiHelper.hkiff.deleteAcaBannerById(id)
              );
              const results = await Promise.all(promises);
              console.log(results);
              toast.dark(`${ids.length} banner(s) deleted.`);
              getBanner();
            } catch (err) {
              console.log(err);
            }
          }
        } catch (err) {
          console.log(err);
        }
        return;
      default:
        return;
    }
  }, [actionType, selectedFlatRows, getBanner]);

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
        {"Banner"}
      </Typography>
      <form>
        <StatusFilter>
          <li>
            <button
              className={status === "all" ? "active" : ""}
              type="button"
              onClick={statusFilterHandler("all")}
            >
              <Typography variant="p">All ({elementCounts.all})</Typography>
            </button>
          </li>
          <li>
            <button
              className={status === "active" ? "active" : ""}
              type="button"
              onClick={statusFilterHandler("active")}
            >
              <Typography variant="p">
                Active ({elementCounts.active})
              </Typography>
            </button>
          </li>
          <li>
            <button
              className={status === "inactive" ? "active" : ""}
              type="button"
              onClick={statusFilterHandler("inactive")}
            >
              <Typography variant="p">
                Inactive ({elementCounts.inactive})
              </Typography>
            </button>
          </li>
        </StatusFilter>
        <Grid
          container
          justify="space-between"
          style={{ paddingBottom: "10px" }}
        >
          <Grid container item xs={6} md={6}>
            <Box
              width="100%"
              component="span"
              display="flex"
              alignItems="center"
            >
              <Box flex>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <InputLabel id="demo-simple-select-outlined-label">
                    Bulk Actions
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    value={actionType}
                    onChange={handleSelectChange}
                    label="Bulk Actions"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value={"DELETE"}>Delete</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box flex>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBulkAction}
                  disabled={disabled}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid container item xs={6} md={6}>
            <Box
              width="100%"
              component="span"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Box display="flex">
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  component={RouterLink}
                  disabled={disabled}
                  to={`${match.url}/new`}
                >
                  Add Banner
                </Button>
              </Box>
              <Box display="flex">
                <TextField
                  id="outlined-error"
                  label="Search"
                  variant="outlined"
                  margin="dense"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </Box>
              <Box display="flex">
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  margin="dense"
                  onClick={triggerSearch}
                >
                  Search
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
        <Grid container xs={12}>
          <TableContainer
            component={Paper}
            style={{
              boxShadow: "none",
              border: "1px solid #cccccc",
            }}
          >
            <fieldset disabled={disabled} className="fieldset">
              <Table
                aria-label="simple table"
                component="ul"
                style={{ margin: "0" }}
                {...getTableProps()}
              >
                <colgroup>
                  <col style={{ width: "1%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <TableHead component="div">
                  {headerGroups.map((headerGroup) => (
                    <TableRow {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <TableCell
                          {...column.getHeaderProps()}
                          className={`${column.className ?? ""}`}
                          style={{
                            paddingTop: 6,
                            paddingBottom: 6,
                            lineHeight: "1.1em",
                            height: "48px",
                          }}
                        >
                          {column.render("Header")}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableHead>
                <TableBody>
                  {rows.map((row, index) => {
                    prepareRow(row);
                    return (
                      <TableRow {...row.getRowProps()}>
                        {row.cells.map((cell) => {
                          return (
                            <TableCell
                              {...cell.getCellProps()}
                              className={`${cell.column.className ?? ""}`}
                              style={{
                                paddingTop: 6,
                                paddingBottom: 6,
                                lineHeight: "1.1em",
                                height: "48px",
                              }}
                            >
                              {cell.render("Cell")}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </fieldset>
          </TableContainer>
        </Grid>
        <Grid
          container
          justify="flex-end"
          style={{
            paddingRight: "25px",
            marginTop: "15px",
          }}
        >
          <div>
            <div></div>
            <PaginationBarForHandler
              pageSize={pageSize}
              setPageSize={setPageSize}
              setData={setData}
              setTotalPages={setTotalPages}
              paginationHandler={paginationHandler}
              gotoPage={gotoPage}
              pageIndex={pageIndex}
              canPreviousPage={canPreviousPage}
              previousPage={previousPage}
              nextPage={nextPage}
              canNextPage={canNextPage}
              elementCounts={elementCounts}
              status={status}
            />
          </div>
        </Grid>
      </form>
    </div>
  );
};

export default BannerListingPage;
